import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Container } from "react-bootstrap";
import { MARKETING_SALES_COLUMNS } from "./utility";

import {
  emptyString,
  marketingSalesConstants,
  MARKETING_SALES_LABELS,
  MARKETING_SALES_SORTING,
} from "../../constants";
import {
  useSpinner,
  Spinner,
  DataTable,
  datePreferenceUtility,
  paginatedPostRequestBodyForMarketingSales,
  ReportTableSection,
} from "../../../../shared";
import { marketingSalesActions, groupsActions } from "../../redux/actions";
import { storeActions } from "../../../admin-screens/redux/actions";
import MultiCharts from "./multiCharts";
import DatePicker from "./datePicker";

const MarketingSales = (props) => {
  const {
    storesReducer: { allBrands, stores },
    groupsReducer: { groups },
    marketingSalesReducer: {
      salesReport,
      totalSmsDelivered,
      campaignMetricsData,
      totalEmailDelivered,
      campaignMetricsPagination,
    },
    getGroups,
    resetStores,
    getAllBrands,
    getSalesReport,
    getCampaignMetricsData,
    getStoresAgainstBrandId,
    getStoresAgainstGroupId,
    emptyAllGroupsAgainstBrandId,
    getTotalEmailAndSmsDeliveredCount,
  } = props;

  //Initial states
  const [selectedBrand, setSelectedBrand] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [searchString, setSearchString] = useState(emptyString);
  const [dateState, setDateState] = useState(datePreferenceUtility());
  const [sorting, setSorting] = useState(MARKETING_SALES_SORTING);

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy, size) => {
    const requestBody = {
      brandId: selectedBrand?.id,
      storeId: selectedLocation?.id,
      groupId: selectedGroup?.id,
      pagination: campaignMetricsPagination,
      pageNumber: pageNumber,
      fromTime: dateState?.startDate,
      toTime: dateState?.endDate,
      sortBy: sortBy || sorting,
      size: size,
      search: searchString,
    };

    return paginatedPostRequestBodyForMarketingSales(requestBody);
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy, size) => {
    if (sortBy) setSorting(sortBy);
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy, size);
    getCampaignMetricsData(requestBody);
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  const displaySelectedLocation = () => {
    if (selectedLocation?.name) return selectedLocation.name;
    if (selectedGroup?.name) return selectedGroup.name;
    return selectedBrand?.name || emptyString;
  };

  const isSelectedSales = () => {
    return (
      selectedBrand?.id || selectedGroup?.id || selectedLocation?.externalId
    );
  };

  useEffect(() => {
    const body = {
      brandId: selectedBrand?.id,
      storeId: selectedLocation?.id,
      groupId: selectedGroup?.id,
      fromTime: dateState?.startDate,
      toTime: dateState?.endDate,
    };

    if (isSelectedSales()) {
      getSalesReport(body);
      getTotalEmailAndSmsDeliveredCount(body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateState, selectedLocation]);

  //Get data on first render and when change search field
  useEffect(() => {
    if (isSelectedSales()) {
      getData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateState, selectedLocation, searchString]);

  useEffect(() => {
    getAllBrands(true);
    resetStores();
    emptyAllGroupsAgainstBrandId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isShowSpinner = () => {
    return (
      spinner(marketingSalesConstants.GET_SALES_REPORT) &&
      spinner(marketingSalesConstants.GET_MARKETING_SALES)
    );
  };

  return (
    <>
      {isShowSpinner() && <Spinner />}
      <DatePicker
        stores={stores}
        spinner={spinner}
        dateState={dateState}
        allBrands={allBrands}
        setDateState={setDateState}
        selectedBrand={selectedBrand}
        selectedGroup={selectedGroup}
        allGroups={groups?.data ?? []}
        setSelectedBrand={setSelectedBrand}
        setSelectedGroup={setSelectedGroup}
        selectedLocation={selectedLocation}
        getGroupsAgainstBrandId={getGroups}
        setSelectedLocation={setSelectedLocation}
        getStoresAgainstGroupId={getStoresAgainstGroupId}
        getStoresAgainstBrandId={getStoresAgainstBrandId}
      />
      <div className="text-center pt-2 fw-bold">
        <h2>{displaySelectedLocation()}</h2>
      </div>
      {salesReport && (
        <MultiCharts
          isSpinnerRunning={
            spinner(
              marketingSalesConstants.GET_TOTAL_EMAIL_AND_SMS_DELIVERED_COUNT
            )
          }
          salesReport={salesReport}
          campaigns={campaignMetricsData}
          totalSmsDelivered={totalSmsDelivered}
          totalEmailDelivered={totalEmailDelivered}
        />
      )}
      <Container fluid className="pb-3">
        <ReportTableSection
          title={MARKETING_SALES_LABELS.TABLE_TITLE}
          searchString={searchString}
          setSearchString={setSearchString}
        >
          <DataTable
            columns={MARKETING_SALES_COLUMNS}
            data={campaignMetricsData ?? []}
            totalRows={campaignMetricsPagination.totalElements}
            numberOfRowsPerPage={campaignMetricsPagination.pageSize}
            pageNumber={campaignMetricsPagination.pageNumber + 1}
            pageSize={campaignMetricsPagination.pageSize}
            sortedBy={sorting}
            onPageChange={(pageNum) =>
              getData(pageNum, null, campaignMetricsPagination.pageSize)
            }
            onColumnSort={(sortBy) => setSorting(sortBy)}
            onSizeChange={(size, pageNum) => {
              getData(pageNum, null, size);
            }}
            isPaginationDropDownEnable={true}
          />
        </ReportTableSection>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  storesReducer: state.storesReducer,
  groupsReducer: state.groupsReducer,
  marketingSalesReducer: state.marketingSalesReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getCampaignMetricsData: (body) =>
    dispatch(marketingSalesActions.getCampaignMetricsData(body)),
  getAllBrands: (isActiveOnly) =>
    dispatch(storeActions.getAllBrands(isActiveOnly)),
  getStoresAgainstBrandId: (brandId) =>
    dispatch(storeActions.getStoresAgainstBrandId(brandId)),
  getSalesReport: (body) =>
    dispatch(marketingSalesActions.getSalesReport(body)),
  getTotalEmailAndSmsDeliveredCount: (body) =>
    dispatch(marketingSalesActions.getTotalEmailAndSmsDeliveredCount(body)),
  getGroups: (brandId) => dispatch(groupsActions.getGroups(brandId)),
  getStoresAgainstGroupId: (groupId) =>
    dispatch(storeActions.getStoresAgainstGroupId(groupId)),
  resetStores: () => dispatch(storeActions.resetStores()),
  emptyAllGroupsAgainstBrandId: () =>
    dispatch(groupsActions.emptyAllGroupsAgainstBrandId()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(MarketingSales));
